import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiPhone, FiMail, FiUser, FiClock } from "react-icons/fi";
import { UrgencyIcon, Illustration } from "./AlertArt";
import RenewPlan from "../RenewPlan/RenewPlan";
import useLogout from "../../../../hooks/useLogout";
import { getUserProfile } from "../../../../api/auth/user";
import { userSettings } from "../../../../store/slice/auth-slice";

const BRAND = "#0285c7";
const INK = "#2F3A45";
const MUTED = "#6B7683";
const LINE = "#E6EBF0";

// Same Simpld support details the Settings page shows.
const CUSTOMER_CARE = "9042075906";
const SUPPORT_EMAIL = "support@simpld.in";
const SUPPORT_HOURS = "9 AM – 7 PM, Mon–Sat";

const Contact = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-center gap-3">
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
      style={{ background: "#E3F1F9", color: BRAND }}
    >
      <Icon size={16} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide" style={{ color: MUTED }}>
        {label}
      </p>
      {href ? (
        <a
          href={href}
          className="block truncate text-[14px] font-semibold"
          style={{ color: INK }}
        >
          {value}
        </a>
      ) : (
        <p className="truncate text-[14px] font-semibold" style={{ color: INK }}>
          {value}
        </p>
      )}
    </div>
  </div>
);

/**
 * Shown instead of the whole app once a free trial has ended with no plan
 * bought, or a plan has lapsed past its grace period: choose a plan, pay, and
 * carry on. Nothing else is reachable.
 */
const SubscriptionLockScreen = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const logout = useLogout();

  // After paying, reload the profile: `restricted` clears and the app opens.
  const unlock = useCallback(async () => {
    try {
      const response = await getUserProfile();
      dispatch(
        userSettings({
          ...response.data,
          token: localStorage.getItem("token"),
        })
      );
    } catch (e) {
      window.location.reload();
    }
  }, [dispatch]);

  const managerName = (auth?.account_manager_name || "").trim();
  const managerPhone = (auth?.account_manager_phone || "").trim();
  const tel = (v) => `tel:${v.replace(/[^\d+]/g, "")}`;

  return (
    <div
      className="min-h-screen w-full overflow-y-auto px-3 py-6 sm:px-6 sm:py-10"
      style={{ background: "#F3F9FD" }}
    >
      <div className="mx-auto w-full max-w-[720px]">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-[18px] font-bold" style={{ color: BRAND }}>
            Simpld
          </span>
          <button
            type="button"
            onClick={logout}
            className="text-[14px] font-medium"
            style={{ color: MUTED }}
          >
            Log out
          </button>
        </div>

        <div
          className="overflow-hidden rounded-3xl bg-white"
          style={{ boxShadow: "0 24px 60px rgba(47,58,69,0.12)" }}
        >
          <div
            className="px-6 py-8 sm:px-10 sm:py-10"
            style={{
              background: "linear-gradient(135deg, #FBEAF8 0%, #FEF6FD 100%)",
            }}
          >
            <div className="relative flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:pr-[140px]">
              <div className="pointer-events-none absolute right-0 top-1/2 hidden w-[130px] -translate-y-1/2 sm:block">
                <Illustration kind="time" accent="#B93FA3" tint="#F6D6F0" />
              </div>
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white"
                style={{
                  color: "#B93FA3",
                  boxShadow: "0 4px 14px rgba(47,58,69,0.08)",
                }}
              >
                <UrgencyIcon kind="time" color="#B93FA3" size={32} />
              </div>
              <div>
                <h1
                  className="text-[22px] font-semibold leading-snug sm:text-[26px]"
                  style={{ color: INK }}
                >
                  {auth?.access_reason === "subscription"
                    ? "Your subscription has ended"
                    : "Your free trial has ended"}
                </h1>
                <p className="mt-1 text-[15px]" style={{ color: MUTED }}>
                  {auth?.access_reason === "subscription"
                    ? "Your account is paused. Renew your plan to pick up exactly where you left off — your clinic data is safe."
                    : "Choose a plan to continue. Your clinic data is safe and will be right where you left it."}
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-7 sm:px-10">
            <RenewPlan onClose={unlock} />
          </div>

          <div
            className="border-t px-6 py-6 sm:px-10"
            style={{ borderColor: LINE, background: "#FAFCFD" }}
          >
            <p
              className="mb-4 text-[12px] font-semibold uppercase tracking-wider"
              style={{ color: MUTED }}
            >
              Need help? We're here
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Contact
                icon={FiPhone}
                label="Customer care"
                value={CUSTOMER_CARE}
                href={tel(CUSTOMER_CARE)}
              />
              <Contact
                icon={FiMail}
                label="Email"
                value={SUPPORT_EMAIL}
                href={`mailto:${SUPPORT_EMAIL}`}
              />
              <Contact
                icon={FiUser}
                label="Account manager"
                value={
                  managerName && managerPhone
                    ? `${managerName} · ${managerPhone}`
                    : managerName || managerPhone || "Not assigned"
                }
                href={managerPhone ? tel(managerPhone) : undefined}
              />
              <Contact icon={FiClock} label="Support hours" value={SUPPORT_HOURS} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionLockScreen;
